import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '@/contexto/AuthContexto';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { CreditCard, Landmark, ArrowRight, ArrowLeft, Shield, Globe, XCircle, Gift, Crown } from 'lucide-react';
import { toast } from 'sonner';

const MOTIVOS_CANCELACION = [
  { key: 'muy_caro', label: 'Es muy caro para mi', anticupon: true },
  { key: 'no_lo_uso', label: 'No lo uso lo suficiente', anticupon: false },
  { key: 'cambie_sistema', label: 'Cambie a otro sistema', anticupon: false },
  { key: 'no_funciona', label: 'No funciona como esperaba', anticupon: false },
  { key: 'cierre_negocio', label: 'Cerre / pause mi negocio', anticupon: false },
  { key: 'otro', label: 'Otro motivo', anticupon: false },
];

export default function Suscripcion() {
  const { usuario } = useAuth();
  const navigate = useNavigate();
  const [cargando, setCargando] = useState(false);
  const [cargandoStripe, setCargandoStripe] = useState(false);
  const [info, setInfo] = useState(null);
  const [banco, setBanco] = useState({ clabe: '', banco: '' });
  const [pasarela, setPasarela] = useState(null);
  const [miPlan, setMiPlan] = useState(null);
  const [esSucursal, setEsSucursal] = useState(false);
  const [planPendiente, setPlanPendiente] = useState(null);
  const [planPendienteDesde, setPlanPendienteDesde] = useState(null);
  const [dialogCancelar, setDialogCancelar] = useState(false);
  const [dialogAntiChurn, setDialogAntiChurn] = useState(false);
  const [motivo, setMotivo] = useState('');
  const [comentario, setComentario] = useState('');
  const [procesandoCancel, setProcesandoCancel] = useState(false);

  useEffect(() => {
    api.get('/pagos/mi-suscripcion').then(r => setInfo(r.data)).catch(() => {});
    api.get('/admin/datos-bancarios').then(r => setBanco(r.data)).catch(() => {});
    api.get('/pagos/pasarela-pago').then(r => setPasarela(r.data)).catch(() => {});
    api.get('/mi-plan').then(r => {
      setMiPlan(r.data.plan);
      setEsSucursal(!!r.data.es_sucursal);
      setPlanPendiente(r.data.plan_pendiente || null);
      setPlanPendienteDesde(r.data.plan_pendiente_desde || null);
    }).catch(() => {});
  }, []);

  const iniciarCancelacion = () => {
    setDialogCancelar(true);
    setMotivo('');
    setComentario('');
  };

  const confirmarCancelacion = async () => {
    if (!motivo) { toast.error('Selecciona un motivo'); return; }
    const opcion = MOTIVOS_CANCELACION.find(m => m.key === motivo);
    // Si el motivo activa anti-churn y no ha usado cupon → oferta retencion primero
    if (opcion?.anticupon && !info?.cupon_retencion_usado) {
      setDialogCancelar(false);
      setDialogAntiChurn(true);
      return;
    }
    await ejecutarCancelacion();
  };

  const ejecutarCancelacion = async () => {
    setProcesandoCancel(true);
    try {
      await api.post('/suscripcion/cancelar', { motivo, comentario });
      toast.success('Suscripcion cancelada. Mantienes acceso hasta el fin de tu periodo.');
      setDialogCancelar(false);
      setDialogAntiChurn(false);
      // Recargar info
      api.get('/pagos/mi-suscripcion').then(r => setInfo(r.data));
    } catch (e) { toast.error(e.response?.data?.detail || 'Error al cancelar'); }
    finally { setProcesandoCancel(false); }
  };

  const aceptarCupon = async () => {
    setProcesandoCancel(true);
    try {
      await api.post('/suscripcion/aplicar-cupon-retencion');
      toast.success('¡Cupon aplicado! Tienes 50% de descuento por 2 meses.');
      setDialogAntiChurn(false);
      setDialogCancelar(false);
      api.get('/pagos/mi-suscripcion').then(r => setInfo(r.data));
    } catch (e) { toast.error(e.response?.data?.detail || 'Error al aplicar cupon'); }
    finally { setProcesandoCancel(false); }
  };

  const iniciarPagoMP = async () => {
    setCargando(true);
    try {
      const origin = window.location.origin;
      const res = await api.post('/pagos/crear-suscripcion', { origin_url: origin });
      if (res.data?.url) {
        window.location.href = res.data.url;
        return;
      }
      toast.error('No se recibio enlace de pago');
    } catch (err) {
      const msg = err.response?.data?.detail || err.message || 'Error desconocido';
      toast.error(`Error al iniciar el pago: ${msg}`);
    }
    setCargando(false);
  };

  const abrirPortalStripe = async () => {
    setCargandoStripe(true);
    try {
      const r = await api.post('/pagos/stripe/portal-session',
        { return_url: window.location.href });
      window.location.href = r.data.url;
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Aún no tienes suscripción Stripe activa. Realiza tu primer pago primero.');
    } finally { setCargandoStripe(false); }
  };

  const iniciarPagoStripe = async () => {
    setCargandoStripe(true);
    try {
      const origin = window.location.origin;
      const res = await api.post('/pagos/stripe/crear-checkout', { origin_url: origin });
      if (res.data?.url) {
        window.location.href = res.data.url;
        return;
      }
      toast.error('No se recibio enlace de pago de Stripe');
    } catch (err) {
      const msg = err.response?.data?.detail || err.message || 'Error desconocido';
      toast.error(`Error al iniciar Stripe: ${msg}`);
    }
    setCargandoStripe(false);
  };

  const diasRestantes = info?.vence
    ? Math.max(0, Math.ceil((new Date(info.vence) - new Date()) / (1000 * 60 * 60 * 24)))
    : null;

  const esMexico = !pasarela || pasarela.pais === 'MX';

  return (
    <div className="space-y-6 animate-fadeIn" data-testid="suscripcion-page">
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" onClick={() => navigate(-1)} className="text-slate-400 hover:text-white">
          <ArrowLeft className="w-5 h-5" />
        </Button>
        <h1 className="font-barlow text-3xl font-bold uppercase tracking-tight text-slate-50">Mi Suscripcion</h1>
      </div>

      {info && (
        <Card className="bg-slate-900/40 border-slate-800">
          <CardContent className="p-6">
            <div className="flex items-center justify-between mb-4">
              <div>
                <p className="text-xs uppercase tracking-wider text-slate-500">Plan Actual</p>
                <div className="flex items-center gap-2">
                  <p className="text-xl font-bold text-slate-100 capitalize font-barlow">
                    {miPlan?.nombre || (info.plan === 'prueba' ? 'Prueba Gratis' : 'Plan Mensual')}
                  </p>
                  {miPlan && <Crown className="w-4 h-4 text-amber-400" />}
                </div>
                {miPlan && (
                  <p className="text-xs text-slate-500 mt-0.5">
                    ${miPlan.precio_promo}/mes primeros {miPlan.meses_promo} meses · luego ${miPlan.precio_regular}
                  </p>
                )}
              </div>
              <div className="text-right">
                <p className={`text-sm font-semibold ${info.estado === 'activo' ? 'text-emerald-400' : info.estado === 'en_gracia' ? 'text-amber-400' : 'text-red-400'}`}>
                  {info.estado === 'activo' ? 'Activo' : info.estado === 'en_gracia' ? 'En gracia' : info.estado === 'expirado' ? 'Expirado' : info.estado}
                </p>
                {diasRestantes !== null && info.estado === 'activo' && (
                  <p className="text-xs text-slate-500">{diasRestantes} dias restantes</p>
                )}
              </div>
            </div>
            {info.vence && (
              <p className="text-sm text-slate-400">
                Vence: <span className="text-slate-200">{new Date(info.vence).toLocaleDateString('es-MX', { day: '2-digit', month: 'long', year: 'numeric' })}</span>
              </p>
            )}
            {info.cupon_retencion_usado && (
              <div className="mt-2 inline-flex items-center gap-1.5 bg-amber-500/10 border border-amber-500/30 rounded-sm px-3 py-1">
                <Gift className="w-3.5 h-3.5 text-amber-400" />
                <span className="text-xs text-amber-400 font-medium">Cupon 50% off activo</span>
              </div>
            )}
            <div className="flex items-center gap-2 mt-3">
              {!esSucursal && (
                <Link to="/planes">
                  <Button variant="outline" size="sm" className="border-slate-700 text-slate-300 hover:bg-slate-800" data-testid="ver-planes-btn">
                    <Crown className="w-3.5 h-3.5 mr-1.5" /> Cambiar de plan
                  </Button>
                </Link>
              )}
              {info.estado === 'activo' && !info.cancelacion_solicitada && !esSucursal && (
                <Button variant="ghost" size="sm" onClick={iniciarCancelacion}
                  className="text-slate-500 hover:text-red-400" data-testid="cancelar-suscripcion-btn">
                  <XCircle className="w-3.5 h-3.5 mr-1.5" /> Cancelar suscripcion
                </Button>
              )}
              {info.cancelacion_solicitada && (
                <span className="text-xs text-amber-400">
                  Cancelacion agendada. Acceso hasta {info.vence}
                </span>
              )}
            </div>
            {esSucursal && (
              <p className="text-[11px] text-slate-500 mt-2" data-testid="sucursal-info">
                Sucursal — hereda plan y facturación del corporativo.
              </p>
            )}
            {planPendiente && (
              <div className="mt-3 inline-flex items-center gap-2 bg-cyan-500/10 border border-cyan-800 rounded-sm px-3 py-1.5" data-testid="plan-pendiente-info">
                <ArrowRight className="w-3.5 h-3.5 text-cyan-400" />
                <span className="text-xs text-cyan-300">
                  Cambio a <span className="font-bold">{planPendiente.nombre}</span>
                  {planPendienteDesde && ` el ${new Date(planPendienteDesde).toLocaleDateString('es-MX', { day: '2-digit', month: 'long' })}`}
                </span>
              </div>
            )}
            {info.ultimo_pago && (
              <p className="text-sm text-slate-400 mt-1">
                Ultimo pago: <span className="text-slate-200">{new Date(info.ultimo_pago).toLocaleDateString('es-MX')}</span>
              </p>
            )}
            {info.suscripcion_recurrente && (
              <div className="mt-2 inline-flex items-center gap-1.5 bg-emerald-500/10 border border-emerald-500/30 rounded-sm px-3 py-1">
                <CreditCard className="w-3.5 h-3.5 text-emerald-400" />
                <span className="text-xs text-emerald-400 font-medium">
                  Cobro automatico activo {info.pasarela_activa === 'stripe' ? '(Stripe)' : info.pasarela_activa === 'mercadopago' ? '(Mercado Pago)' : ''}
                </span>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Portal Stripe - solo visible si tiene suscripción activa */}
      {info && !esSucursal && ['activo', 'en_gracia'].includes(info.estado) && (
        <Card className="bg-gradient-to-br from-indigo-500/10 to-cyan-500/5 border-indigo-700/50" data-testid="card-portal-stripe">
          <CardContent className="p-6">
            <div className="flex items-start justify-between gap-4 flex-wrap">
              <div className="flex-1 min-w-[240px]">
                <div className="flex items-center gap-2 mb-2">
                  <CreditCard className="w-5 h-5 text-cyan-400" />
                  <p className="text-sm font-bold text-slate-100">Administra tu Suscripción</p>
                </div>
                <p className="text-xs text-slate-400 leading-relaxed">
                  Actualiza tu tarjeta, ve tus facturas, cambia método de pago o cancela cuando quieras.
                  Todo desde el portal seguro de Stripe.
                </p>
              </div>
              <Button onClick={abrirPortalStripe} disabled={cargandoStripe}
                className="bg-cyan-500 hover:bg-cyan-600 text-black font-bold rounded-sm"
                data-testid="portal-stripe-btn">
                {cargandoStripe ? 'Abriendo...' : (
                  <>Abrir Portal <ArrowRight className="w-4 h-4 ml-2" /></>
                )}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Ocultar opciones de pago para sucursales — el corporativo paga */}
        {!esSucursal && (
        <><Card className="bg-slate-900/40 border-indigo-700/50 relative overflow-hidden" data-testid="card-stripe">
          <div className="absolute top-0 left-0 right-0 h-1 bg-indigo-500" />
          <CardContent className="p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <Shield className="w-6 h-6 text-indigo-400" />
                <h3 className="font-barlow font-bold text-lg text-slate-100">Opcion 1 — Stripe</h3>
              </div>
              <Globe className="w-4 h-4 text-slate-500" />
            </div>
            <p className="text-3xl font-bold text-indigo-400 font-barlow mb-1" data-testid="precio-stripe">
              {pasarela?.precio ? `$${pasarela.precio}` : '$---'} <span className="text-sm text-slate-500 font-normal">{pasarela?.moneda || 'USD'} / mes</span>
            </p>
            <p className="text-sm text-slate-400 mb-4">Tarjeta internacional. Visa, Mastercard, Amex.</p>
            <Button onClick={iniciarPagoStripe} disabled={cargandoStripe}
              className="w-full bg-indigo-500 hover:bg-indigo-600 text-white font-bold rounded-sm h-11"
              data-testid="suscripcion-stripe-btn">
              {cargandoStripe ? 'Procesando...' : 'Pagar con Stripe'} <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </CardContent>
        </Card>

        {/* Opcion 2: Mercado Pago — visible solo para Mexico */}
        {esMexico && (
          <Card className="bg-slate-900/40 border-amber-700/50 relative overflow-hidden" data-testid="card-mercadopago">
            <div className="absolute top-0 left-0 right-0 h-1 bg-amber-500" />
            <CardContent className="p-6">
              <div className="flex items-center gap-3 mb-4">
                <CreditCard className="w-6 h-6 text-amber-500" />
                <h3 className="font-barlow font-bold text-lg text-slate-100">Opcion 2 — Mercado Pago</h3>
              </div>
              <p className="text-3xl font-bold text-amber-500 font-barlow mb-1" data-testid="precio-mp">
                ${info?.precio || 399} <span className="text-sm text-slate-500 font-normal">{info?.moneda || 'MXN'} / mes</span>
              </p>
              <p className="text-sm text-slate-400 mb-4">Cobro automatico mensual. Tarjetas MX y OXXO.</p>
              <Button onClick={iniciarPagoMP} disabled={cargando}
                className="w-full bg-amber-500 hover:bg-amber-600 text-black font-bold rounded-sm h-11"
                data-testid="suscripcion-pagar-btn">
                {cargando ? 'Procesando...' : 'Suscribirme con MP'} <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Opcion 3: Deposito bancario — solo Mexico */}
        {esMexico && (
          <Card className="bg-slate-900/40 border-slate-800" data-testid="card-deposito">
            <CardContent className="p-6">
              <div className="flex items-center gap-3 mb-4">
                <Landmark className="w-6 h-6 text-emerald-400" />
                <h3 className="font-barlow font-bold text-lg text-slate-100">Opcion 3 — Deposito Bancario</h3>
              </div>
              <p className="text-3xl font-bold text-emerald-400 font-barlow mb-1" data-testid="precio-banco">
                ${info?.precio || 399} <span className="text-sm text-slate-500 font-normal">{info?.moneda || 'MXN'} / mes</span>
              </p>
              <div className="bg-slate-800/50 rounded-sm p-3 text-left mt-3">
                <div className="space-y-1 text-sm text-slate-300">
                  <p>Banco: <span className="text-slate-100 font-medium">{banco.banco || 'BBVA'}</span></p>
                  <p>CLABE: <span className="text-slate-100 font-medium font-mono" data-testid="clabe-display">{banco.clabe || '---'}</span></p>
                  <p>Referencia: <span className="text-amber-400 font-medium">{usuario?.email}</span></p>
                </div>
                <p className="text-xs text-slate-500 mt-2">Activacion en menos de 24 horas.</p>
              </div>
            </CardContent>
          </Card>
        )}
        </>
        )}
        {esSucursal && (
          <div className="md:col-span-2 bg-slate-900/40 border border-slate-800 rounded-sm p-6 text-center" data-testid="sucursal-facturacion-info">
            <Landmark className="w-8 h-8 text-slate-500 mx-auto mb-2" />
            <p className="text-sm text-slate-300 font-medium">La cuenta corporativa gestiona la facturación</p>
            <p className="text-xs text-slate-500 mt-1">
              Todos los cobros (plan + sucursales) se procesan desde la cuenta principal.
            </p>
          </div>
        )}
      </div>

      {/* Dialog cancelacion — motivo */}
      <Dialog open={dialogCancelar} onOpenChange={setDialogCancelar}>
        <DialogContent className="bg-slate-900 border-slate-800 max-w-md" data-testid="dialog-cancelar">
          <DialogHeader>
            <DialogTitle className="font-barlow text-slate-100">Cancelar suscripcion</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <p className="text-sm text-slate-400">Antes de irte, cuéntanos por qué. Nos ayuda a mejorar.</p>
            <div className="space-y-1.5">
              {MOTIVOS_CANCELACION.map(m => (
                <label key={m.key} className="flex items-center gap-2 p-2.5 rounded-sm border border-slate-800 hover:bg-slate-800/40 cursor-pointer">
                  <input type="radio" name="motivo" value={m.key}
                    checked={motivo === m.key} onChange={() => setMotivo(m.key)}
                    className="accent-cyan-500" data-testid={`motivo-${m.key}`} />
                  <span className="text-sm text-slate-200">{m.label}</span>
                </label>
              ))}
            </div>
            <Textarea value={comentario} onChange={e => setComentario(e.target.value)}
              placeholder="Comentario opcional..." className="bg-slate-950 border-slate-700 text-slate-200" rows={2}
              data-testid="comentario-cancel" />
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setDialogCancelar(false)} className="text-slate-400">
              Mantener suscripcion
            </Button>
            <Button onClick={confirmarCancelacion} disabled={!motivo || procesandoCancel}
              className="bg-red-500 hover:bg-red-600 text-white" data-testid="confirmar-cancel-btn">
              Continuar cancelacion
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog anti-churn — cupon 50% off */}
      <Dialog open={dialogAntiChurn} onOpenChange={setDialogAntiChurn}>
        <DialogContent className="bg-slate-900 border-amber-800 max-w-md" data-testid="dialog-antichurn">
          <DialogHeader>
            <DialogTitle className="font-barlow text-slate-100 flex items-center gap-2">
              <Gift className="w-5 h-5 text-amber-400" /> ¡Espera! Tenemos una oferta para ti
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="bg-gradient-to-br from-amber-500/20 to-amber-500/5 border border-amber-600 rounded-sm p-5 text-center">
              <p className="text-xs uppercase tracking-wider text-amber-400 mb-1">Oferta exclusiva</p>
              <p className="text-4xl font-bold text-amber-400 font-barlow">50% OFF</p>
              <p className="text-sm text-slate-300 mt-1">por los proximos <span className="font-bold">2 meses</span></p>
            </div>
            <p className="text-sm text-slate-300 leading-relaxed">
              Sabemos que el precio importa. Como cliente valioso, te regalamos <span className="font-bold text-amber-400">50% de descuento por 2 meses</span> para
              que sigas creciendo con nosotros. Sin compromiso, cancelas cuando quieras.
            </p>
            <div className="text-xs text-slate-500 text-center">Este descuento se aplica una sola vez.</div>
          </div>
          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button variant="ghost" onClick={ejecutarCancelacion} disabled={procesandoCancel}
              className="text-slate-500 hover:text-slate-300 text-xs" data-testid="rechazar-cupon-btn">
              No gracias, cancelar de todos modos
            </Button>
            <Button onClick={aceptarCupon} disabled={procesandoCancel}
              className="bg-amber-500 hover:bg-amber-600 text-black font-bold" data-testid="aceptar-cupon-btn">
              <Gift className="w-4 h-4 mr-2" /> Aceptar 50% off
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
